/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { cn } from '@/shared/ui/utils/tailwind';

export interface AreaIntroPanelProps {
  body: string;
  /** Optional heading; omit when the page title already names the area. */
  title?: string;
  /** `card` is a bordered panel; `plain` nests inside another surface. */
  variant?: 'card' | 'plain';
  testId?: string;
}

/**
 * Didactic copy explaining what an area is for.
 *
 * @param props - Localized body and optional title.
 * @returns Intro panel content.
 */
export function AreaIntroPanel({
  body,
  title,
  variant = 'plain',
  testId = 'area-intro',
}: AreaIntroPanelProps) {
  const isCard = variant === 'card';

  return (
    <aside
      data-testid={testId}
      className={cn(
        'flex min-w-0 flex-col gap-(--space-1)',
        isCard &&
          'rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-4)] shadow-[var(--shadow-sm)]',
      )}
    >
      {title ? (
        <h2 className="text-[length:var(--text-body-lg)] font-semibold leading-[var(--leading-snug)] text-[var(--text-primary)]">
          {title}
        </h2>
      ) : null}
      <p className="text-[length:var(--text-small)] leading-[var(--leading-normal)] text-[var(--text-secondary)]">
        {body}
      </p>
    </aside>
  );
}
